import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { BarChart3, CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import { formatDate } from '../../lib/format';

const GRADE_COLOR = { A: 'text-emerald-700 bg-emerald-50', B: 'text-[#3b4872] bg-[#3b4872]/10', C: 'text-amber-700 bg-amber-50', D: 'text-red-600 bg-red-50' };

const MetricBar = ({ label, value, max = 100, format = v => `${v}%` }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="font-bold text-slate-900">{format(value)}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#3b4872] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function VendorPerformance() {
  const { vendorFetch } = useVendorAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    vendorFetch('/partner/performance').then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 py-8">Loading…</div>;

  const { scorecard, rating } = data;
  const grade = scorecard?.grade || rating?.grade || null;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="vendor-performance-page">
      <div>
        <h1 className="font-bold text-xl text-slate-900">Performance</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your delivery and quality scorecard as seen by Ellora EPC.</p>
      </div>

      {!scorecard && !rating ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <BarChart3 size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-semibold">No performance data yet</div>
          <div className="text-xs mt-1">Scores appear after your first orders are completed and assessed.</div>
        </div>
      ) : (
        <>
          {/* Overall grade */}
          {grade && (
            <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-center gap-5">
              <div className={`text-5xl font-extrabold px-4 py-2 rounded-sm ${GRADE_COLOR[grade] || 'text-slate-600 bg-slate-100'}`}>{grade}</div>
              <div>
                <div className="font-bold text-slate-900">Overall Grade</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {grade === 'A' ? 'Excellent — preferred vendor' :
                   grade === 'B' ? 'Good standing' :
                   grade === 'C' ? 'Improvement needed' :
                   'Performance concerns — please contact procurement'}
                </div>
              </div>
            </div>
          )}

          {/* Rating metrics */}
          {rating && (
            <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
              <div className="font-bold text-sm">Rating Metrics</div>
              {rating.on_time_pct != null && <MetricBar label="On-Time Delivery" value={rating.on_time_pct} />}
              {rating.quality_rejection_pct != null && <MetricBar label="Quality Rejection Rate" value={rating.quality_rejection_pct} format={v => `${v}%`} />}
              {rating.shortage_pct != null && <MetricBar label="Shortage Rate" value={rating.shortage_pct} format={v => `${v}%`} />}
              {rating.overall_score != null && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-700">Overall Score</span>
                    <span className="text-xl font-extrabold text-[#3b4872]">{rating.overall_score}/100</span>
                  </div>
                </div>
              )}
              {rating.total_orders != null && (
                <div className="text-xs text-slate-400">Based on {rating.total_orders} order(s)
                  {rating.last_updated && ` · Updated ${formatDate(rating.last_updated)}`}
                </div>
              )}
            </div>
          )}

          {/* Scorecard detail */}
          {scorecard && Object.keys(scorecard).filter(k => !['id', 'vendor_id', 'grade'].includes(k)).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="font-bold text-sm mb-3">Detailed Scorecard</div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                {Object.entries(scorecard).filter(([k]) => !['id', 'vendor_id', '_id'].includes(k)).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 border-b border-slate-100">
                    <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="font-semibold text-slate-800">{typeof v === 'number' ? v.toFixed(1) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
